import { Grid, TextField } from '@mui/material'
import { DateValidationError } from '@mui/x-date-pickers'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { hideDrawer } from '../../../../redux/reducers/appSettingsReducer'
import { showErrorMessage, showSuccessMessage } from '../../../../redux/reducers/snackbarReducer'
import { RootStore, useAppDispatch } from '../../../../redux/store'
import { manualRequestsService } from '../../../../services/myActionsService'
import Button from '../../../../shared/UI/Button'
import DatePicker from '../../../../shared/UI/DatePickerV2'
import DropdownMenu from '../../../../shared/UI/DropdownMenu'
import Paragraph from '../../../../shared/UI/Paragraph'
import Tab from '../../../../shared/UI/Tab'
import {
  AbsenceExpectedReturnUpdateRequest,
  DiscountAbsenceRequest,
} from '../../../../types/absence'
import { BaseResponse } from '../../../../types/base-response'
import { getSelectValuesByType } from '../../../../utils/app-utils'
import UserErrorMessage from '../../../../utils/errorFilter'
import InfoBanner from '../../../../shared/UI/InfoBanner'
import { absenceFormLabels } from '../../../../utils/constants'

const TAB_INDICES = {
  EXPECTED_RETURN: 0,
  DISCOUNT_ABSENCE: 1,
  DELETE_ABSENCE: 2,
}

type Props = {
  absenceData: any
  hasFutureSicknessAbsence: boolean
  discountAbsenceOnly?: boolean
  onSubmit: () => void
  onCancel: () => void
  onUpdate: (field: string, value: any) => void
  getAbsenceData: () => void
}

function OccurrenceActions({ absenceData, hasFutureSicknessAbsence, discountAbsenceOnly = false, onCancel, onUpdate, getAbsenceData }: Props) {
  const dispatch = useAppDispatch()

  const navigate = useNavigate()
  const [validationErrors, setValidationErrors] = useState<any>({})
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteDescription, setDeleteComment] = useState('')
  const [expectedReturn, setexpectedReturn] = useState<Date | null>(new Date(absenceData.expectedReturn))
  const [discountAbsenceFrom, setdiscountAbsenceFrom] = useState(absenceData.startDate)
  const [discountAbsenceTo, setdiscountAbsenceTo] = useState(absenceData.endDate)
  const [expectedReturnChanged, setExpectedReturnChanged] = useState(false)
  const [expectedReturnError, setExpectedReturnError] = useState<DateValidationError>(null)
  const [discountAbsenceFromError, setDiscountAbsenceFromError] = useState<DateValidationError>(null)
  const [discountAbsenceToError, setDiscountAbsenceToError] = useState<DateValidationError>(null)
  const [isExpectedReturnLoading, setIsExpectedReturnLoading] = useState<boolean>(false)
  const [isDiscountAbsenceLoading, setIsDiscountAbsenceLoading] = useState<boolean>(false)

  const location = useLocation()

  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )

  const activeRoute = useSelector<RootStore, string>(
    (state: RootStore) => state.appSettings.activeRoute
  )

  useEffect(() => {
    setdiscountAbsenceFrom(new Date(format(new Date(discountAbsenceFrom), 'yyyy-MM-dd')))
    setdiscountAbsenceTo(new Date(format(new Date(discountAbsenceTo), 'yyyy-MM-dd')))
  }, [])

  function submitExpectedReturn() {
    if (!expectedReturnChanged || !expectedReturn) {
      return
    }

    const absenceObj: AbsenceExpectedReturnUpdateRequest = {
      absenceId: absenceData.id,
      expectedReturn: format(expectedReturn, 'yyyy-MM-dd'),
    }

    setIsExpectedReturnLoading(true)
    manualRequestsService
      .updateExpectedReturn(absenceObj)
      .then(res => {
        onUpdate('expectedReturn', expectedReturn)
        dispatch(showSuccessMessage('Success!'))
        dispatch(hideDrawer())
      })
      .catch(err => {
        const response: BaseResponse = err.response.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
      .finally(() => setIsExpectedReturnLoading(false))
  }

  function submitDiscountAbsence() {
    const absenceObj: DiscountAbsenceRequest = {
      absenceId: absenceData.id,
      dateFrom: format(discountAbsenceFrom, 'yyyy-MM-dd'),
      dateTo: format(discountAbsenceTo, 'yyyy-MM-dd'),
    }

    setIsDiscountAbsenceLoading(true)
    manualRequestsService
      .createDiscountAbsenceDay(absenceObj)
      .then(res => {
        getAbsenceData()
        dispatch(showSuccessMessage('Successfully discounted absence days'))
        dispatch(hideDrawer())
      })
      .catch(err => {
        const response: BaseResponse = err.response.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
      .finally(() => setIsDiscountAbsenceLoading(false))
  }

  function deleteAbsence() {
    setValidationErrors({})
    const errors: any = {}

    if (deleteDescription.length === 0) {
      errors.deleteDescription = true
    }

    if (deleteReason.length === 0) {
      errors.deleteReason = true
    }

    if (Object.keys(errors).length !== 0) {
      setValidationErrors(errors)
      return
    }

    const absenceObj = {
      absenceId: absenceData.id,
      deleteReason: String(deleteReason),
      deleteDescription,
    }

    manualRequestsService
      .deleteAbsenceOccurrence(absenceObj)
      .then(res => {
        dispatch(showSuccessMessage('Absence occurrence deleted successfully!'))
        dispatch(hideDrawer())
        const returnTo = location.state?.previousLocation || '/myactions'
            navigate(returnTo)
      })
      .catch(err => {
        const response: BaseResponse = err.response.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
  }

  return (
    <>
      <Tab
        tabChange={discountAbsenceOnly ? TAB_INDICES.DISCOUNT_ABSENCE : TAB_INDICES.EXPECTED_RETURN}
        titleAndContent={[
          {
            label: 'Expected Return',
            tabContext: (
              <>
                <Grid container xl={12} spacing={4}>
                  <Grid item xs={12} mt={4}>
                    <Paragraph weight="bold">Details</Paragraph>
                  </Grid>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Set expected return date"
                      dataTestId="expected-return-date"
                      value={expectedReturn}
                      required
                      onChange={e => {
                        setexpectedReturn(e)
                        setExpectedReturnChanged(true)
                      }}
                      onError={setExpectedReturnError}
                    />
                  </Grid>
                  <Grid item xs={12} gap={4} display="flex" justifyContent="flex-end">
                    <Button
                      label="Cancel"
                      color="secondary"
                      variant="outlined"
                      onClick={onCancel}
                    />
                    <Button
                      label="Submit"
                      loading={isExpectedReturnLoading}
                      onClick={() => submitExpectedReturn()}
                      disabled={!expectedReturnChanged || !!expectedReturnError}
                    />
                  </Grid>
                </Grid>
              </>
            ),
            disabled: discountAbsenceOnly,
          },
          {
            label: 'Discount Absence',
            tabContext: (
              <>
                <Grid container xl={12} spacing={4}>
                  <Grid item xs={12} mt={4}>
                    <Paragraph weight="bold">Details</Paragraph>
                  </Grid>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Date From"
                      dataTestId="discount-absence-dateFrom"
                      required
                      value={discountAbsenceFrom}
                      onChange={e => {
                        setdiscountAbsenceFrom(e)
                      }}
                      onError={setDiscountAbsenceFromError}
                      disabled={!userPermissions.includes('DiscountAbsence')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Date To"
                      dataTestId="discount-absence-dateTo"
                      required
                      value={discountAbsenceTo}
                      onChange={e => {
                        setdiscountAbsenceTo(e)
                      }}
                      onError={setDiscountAbsenceToError}
                      disabled={!userPermissions.includes('DiscountAbsence')}
                    />
                  </Grid>
                  <Grid item xs={12} gap={4} display="flex" justifyContent="flex-end">
                    <Button
                      label="Cancel"
                      color="secondary"
                      variant="outlined"
                      onClick={onCancel}
                    />
                    <Button disabled={!!discountAbsenceFromError || !!discountAbsenceToError} loading={isDiscountAbsenceLoading} label="Submit" onClick={() => submitDiscountAbsence()} />
                  </Grid>
                </Grid>
              </>
            ),
            disabled: !userPermissions.includes('DiscountAbsence'),
          },
          {
            label: 'Delete Absence',
            tabContext: (
              <>
                <Grid container xl={12} spacing={4}>
                  { hasFutureSicknessAbsence && (
                    <Grid item xs={12}>
                      <InfoBanner
                        dataTestId="future-sickness-warning"
                        margins={{ top: '16px', bottom: '0', left: '0', right: '0' }}
                        text={absenceFormLabels.deleteAbsenceFutureSicknessAbsenceWarning}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} mt={hasFutureSicknessAbsence ? 0 : 4}>
                    <Paragraph weight="bold">Details</Paragraph>
                  </Grid>
                  <Grid item xs={12}>
                    <DropdownMenu
                      id="reason"
                      label="Reason"
                      textField="displayValue"
                      valueField="value"
                      data={getSelectValuesByType('AbsenceDeletionReason')}
                      onChange={e => {
                        setDeleteReason(e.target.value)
                      }}
                      disabled={!userPermissions.includes('DeleteAttendance')}
                      helperText="Required"
                      error={validationErrors.deleteReason}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="description"
                      label="Description"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={5}
                      value={deleteDescription}
                      onChange={e => {
                        setDeleteComment(e.target.value)
                      }}
                      disabled={!userPermissions.includes('DeleteAttendance')}
                      helperText="Required"
                      error={validationErrors.deleteDescription}
                    />
                  </Grid>
                  <Grid item xs={12} gap={4} display="flex" justifyContent="flex-end">
                    <Button
                      label="Cancel"
                      color="secondary"
                      variant="outlined"
                      onClick={onCancel}
                    />
                    <Button disabled={hasFutureSicknessAbsence} label="Delete Absence" onClick={() => deleteAbsence()} />
                  </Grid>
                </Grid>
              </>
            ),
            disabled: discountAbsenceOnly || !userPermissions.includes('DeleteAttendance'),
          },
        ]}
      />
    </>
  )
}

export default OccurrenceActions
